<template>
  <div class="announcement-center">
    <el-page-header @back="goBack" content="系统公告" />
    
    <div class="header-section">
      <h2>系统公告</h2>
      <div class="header-actions">
        <el-button 
          :disabled="unreadCount === 0" 
          @click="markAllAsRead"
          type="primary"
          round
        >
          全部标记为已读
        </el-button>
      </div>
    </div>
    
    <div class="announcements-list">
      <el-card 
        v-for="item in announcements" 
        :key="item.id" 
        class="announcement-item"
        :class="{ unread: item.isRead === 0 }"
      >
        <div class="announcement-header">
          <div class="announcement-title">
            <el-badge :is-dot="item.isRead === 0" class="announcement-badge">
              <h3>{{ item.title }}</h3>
            </el-badge>
          </div>
          <span class="time">{{ formatDate(item.createTime || item.publishTime) }}</span>
        </div>
        <div 
          class="announcement-content" 
          :class="{ expanded: item.expanded }"
          v-html="item.content"
        ></div>
        <div class="announcement-meta">
          <el-tag type="info" size="small">系统公告</el-tag>
          <span class="status">{{ item.isRead ? '已读' : '未读' }}</span>
          <el-button 
            type="primary" 
            link
            class="expand-btn"
            @click="toggleExpand(item)"
          >
            {{ item.expanded ? '收起' : '展开' }}
          </el-button>
        </div>
      </el-card>
      
      <div v-if="loading" class="loading">
        <div v-for="n in 3" :key="'skeleton'+n" class="skeleton-item">
          <el-skeleton :rows="3" animated />
        </div>
      </div>
      
      <div v-if="announcements.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无系统公告" />
      </div>
    </div>
    
    <el-pagination
      v-if="total > 0"
      class="pagination"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="currentPage"
      :page-sizes="[10, 20, 30, 50]"
      :page-size="pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { userAnnouncementApi } from '@/user/api/announcement';
import { formatDate } from '@/utils/date';

export default defineComponent({
  name: 'AnnouncementCenterView',
  setup() {
    // 响应式数据
    const announcements = ref<any[]>([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const unreadCount = ref(0);

    // 加载公告列表
    const loadAnnouncements = async () => {
      loading.value = true;
      try {
        const response: any = await userAnnouncementApi.getAnnouncementList(
          currentPage.value, 
          pageSize.value
        );

        if (response && 'records' in response && 'total' in response) {
          // 初始化expanded状态
          announcements.value = (response.records || []).map((record: any) => ({
            ...record,
            expanded: false
          }));
          total.value = response.total || 0;
        } else {
          announcements.value = [];
          total.value = 0;
        }
      } catch (error) {
        console.error('[AnnouncementCenter] Error loading announcements:', error);
        ElMessage.error('获取公告列表失败');
        announcements.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // 加载未读数
    const loadUnreadCount = async () => {
      try {
        const response: any = await userAnnouncementApi.getUnreadCount();
        if (typeof response === 'number') {
          unreadCount.value = response;
        } else if (response && typeof response.data !== 'undefined') {
          unreadCount.value = Number(response.data);
        } else {
          unreadCount.value = 0;
        }
      } catch (error) {
        console.error('[AnnouncementCenter] Error loading unread count:', error);
        // ElMessage.error('获取未读公告数失败');
      }
    };

    // 分页处理
    const handleCurrentChange = (val: number) => {
      currentPage.value = val;
      loadAnnouncements();
    };

    const handleSizeChange = (val: number) => {
      pageSize.value = val;
      currentPage.value = 1;
      loadAnnouncements();
    };

    // 切换展开/收起状态
    const toggleExpand = async (item: any) => {
      // 切换展开状态
      item.expanded = !item.expanded;
      
      // 如果是展开并且是未读状态，则标记为已读
      if (item.expanded && item.isRead === 0) {
        try {
          await userAnnouncementApi.markAnnouncementAsRead(item.id);
          item.isRead = 1;
          loadUnreadCount();
        } catch (error) {
          console.error('[AnnouncementCenter] Error marking as read:', error);
        }
      }
    };

    // 全部标记为已读
    const markAllAsRead = async () => {
      try {
        const unreadIds = announcements.value
          .filter(item => item.isRead === 0)
          .map(item => item.id);
        
        if (unreadIds.length > 0) {
          await userAnnouncementApi.batchMarkAsRead(unreadIds);
          ElMessage.success('全部标记为已读');
          loadAnnouncements();
          loadUnreadCount();
        }
      } catch (error) {
        ElMessage.error('批量标记已读失败');
        console.error('[AnnouncementCenter] Error in mark all as read:', error);
      }
    };

    // 返回上一页
    const goBack = () => {
      window.history.back();
    };

    // 格式化时间
    const formatDateMethod = (dateString: string) => {
      return formatDate(dateString);
    };

    onMounted(() => {
      loadAnnouncements();
      loadUnreadCount();
    });

    return {
      announcements,
      loading,
      currentPage,
      pageSize,
      total,
      unreadCount,
      loadAnnouncements,
      handleCurrentChange,
      handleSizeChange,
      toggleExpand,
      markAllAsRead,
      goBack,
      formatDate: formatDateMethod
    };
  }
});
</script>

<style scoped>
.announcement-center {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  background: linear-gradient(135deg, #fefefe 0%, #f8f3eb 100%);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(139, 125, 107, 0.15);
  position: relative;
  overflow: hidden;
  min-height: 80vh;
}

.announcement-center::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(#d4c19c 1px, transparent 1px),
    radial-gradient(#d4c19c 1px, transparent 1px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
  opacity: 0.08;
  z-index: 0;
  pointer-events: none;
}

.announcement-center > * {
  position: relative;
  z-index: 1;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9dcc8;
}

.header-section h2 {
  margin: 0;
  color: #5c5142;
  font-size: 24px;
  font-weight: 600;
  font-family: 'Quicksand', 'Nunito', 'Microsoft YaHei', sans-serif;
}

.header-section .header-actions .el-button {
  margin-left: 10px;
  border-radius: 20px;
  padding: 8px 20px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(212, 131, 65, 0.2);
  transition: all 0.3s ease;
}

.header-section .header-actions .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 131, 65, 0.3);
}

.announcements-list .announcement-item {
  margin-bottom: 15px;
  cursor: pointer;
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.08);
  transition: all 0.3s ease;
  background: #fff;
  border: 1px solid #f0e6d2;
  padding: 18px;
}

.announcements-list .announcement-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(139, 125, 107, 0.15);
}

.announcements-list .announcement-item.unread {
  background: linear-gradient(to right, #fff9f2 0%, #fefefe 100%);
  border-left: 4px solid #d48341;
}

.announcements-list .announcement-item .announcement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.announcements-list .announcement-item .announcement-header .announcement-title {
  flex: 1;
}

.announcements-list .announcement-item .announcement-header .announcement-title h3 {
  margin: 0;
  color: #5c5142;
  font-size: 18px;
  font-weight: 600;
}

.announcements-list .announcement-item .announcement-header .time {
  font-size: 13px;
  color: #8B7D6B;
  white-space: nowrap;
  margin-left: 10px;
}

.announcements-list .announcement-item .announcement-content {
  color: #606266;
  line-height: 1.6;
  margin-bottom: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  border-left: 3px solid #e9dcc8;
  /* 限制显示行数，保持列表整洁 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.announcements-list .announcement-item .announcement-content.expanded {
  /* 展开状态下显示完整内容 */
  display: block;
  -webkit-line-clamp: unset;
  max-height: none;
  overflow: visible;
}

.announcements-list .announcement-item .announcement-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px dashed #eee;
  color: #909399;
  font-size: 12px;
}

.expand-btn {
  color: #d48341;
  font-size: 12px;
  cursor: pointer;
}

.announcements-list .announcement-item .announcement-meta .status {
  font-style: italic;
}

.loading {
  margin: 20px 0;
}

.loading .skeleton-item {
  margin-bottom: 15px;
  padding: 16px;
  border-radius: 12px;
  background: white;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.pagination {
  text-align: center;
  margin-top: 20px;
}

.pagination .el-pager li {
  transition: all 0.3s ease;
}

.pagination .el-pager li:hover {
  color: #d48341;
}

.pagination .el-pager li.active {
  color: #d48341;
  font-weight: 600;
}

.announcement-badge {
  display: block;
}
</style>